// client/src/utils/organizationUtils.js

import { db } from '../firebase';
import { resolveApiBaseUrl } from './apiBaseUrl';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where 
} from 'firebase/firestore';

/**
 * Filter content items by organization assignment.
 * Items without organizationIds are globally available.
 * @param {Array} items
 * @param {Array<string>} userOrganizationIds
 */
export const filterContentForOrganization = (items, userOrganizationIds = []) => {
  return items.filter((item) => {
    const orgIds = item.organizationIds;
    if (!orgIds || orgIds.length === 0) return true;
    if (!userOrganizationIds || userOrganizationIds.length === 0) return false;
    return orgIds.some((id) => userOrganizationIds.includes(id));
  });
};

export const validateUserIdForOrganization = (userId, organization) => {
  if (!organization?.access?.idPrefixes || !userId) {
    return false;
  }
  
  const prefixes = organization.access.idPrefixes;
  return prefixes.some(prefix => userId.toUpperCase().startsWith(prefix.toUpperCase()));
};

/** Whether signup must collect a library card / student ID (from public org API). */
export const organizationRequiresVerifiedId = (org) => Boolean(org?.requiresVerifiedId);

// Fetch all organizations for signup dropdown (public API — no ID prefixes exposed)
export const fetchAllOrganizations = async () => {
  try {
    const response = await fetch(`${resolveApiBaseUrl()}/api/organizations/public`);

    if (!response.ok) {
      throw new Error(`Failed to load organizations: ${response.status}`);
    }

    const data = await response.json();
    return data.organizations || [];
  } catch (error) {
    console.error('Error fetching organizations:', error);
    return [];
  }
};

// Fetch single organization
export const fetchOrganization = async (organizationId) => {
  try {
    const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
    if (orgDoc.exists()) {
      return {
        id: orgDoc.id,
        ...orgDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching organization:', error);
    return null;
  }
};

// Create organization
export const createOrganization = async (organizationData) => {
  try {
    const orgRef = doc(collection(db, 'organizations'));
    const newOrg = {
      ...organizationData,
      createdAt: new Date(),
      isActive: true
    };
    
    await setDoc(orgRef, newOrg);
    return orgRef.id; // Return just the ID string
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

// Update organization
export const updateOrganization = async (organizationId, updates) => {
  try {
    const orgRef = doc(db, 'organizations', organizationId);
    await updateDoc(orgRef, {
      ...updates,
      updatedAt: new Date()
    });
    return true;
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

// Fetch users by organization
export const fetchUsersByOrganization = async (organizationId) => {
  try {
    const usersSnapshot = await getDocs(
      query(
        collection(db, 'users'),
        where('organizationIds', 'array-contains', organizationId)
      )
    );
    
    const users = [];
    usersSnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return users;
  } catch (error) {
    console.error('Error fetching users by organization:', error);
    return [];
  }
};

// Add user to organization
export const addUserToOrganization = async (userId, organizationId, verifiedId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const organizationIds = userData.organizationIds || [];
      const verifiedIds = userData.verifiedIds || {};
      
      // Add organization if not already present
      if (!organizationIds.includes(organizationId)) {
        organizationIds.push(organizationId);
      }
      
      // Store verified ID for this organization
      verifiedIds[organizationId] = verifiedId;
      
      await updateDoc(userRef, {
        organizationIds,
        verifiedIds,
        currentOrganizationId: organizationId, // Set as current org
        lastActiveAt: new Date()
      });
      
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error adding user to organization:', error);
    throw error;
  }
};

// Switch user's current organization context
export const switchUserOrganization = async (userId, organizationId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      currentOrganizationId: organizationId,
      lastActiveAt: new Date()
    });
    return true;
  } catch (error) {
    console.error('Error switching user organization:', error);
    throw error;
  }
};
